import React, { useMemo } from "react";
import { LineChart } from "@mui/x-charts/LineChart";
import dayjs from "dayjs";

import { DashboardEmptyState } from "../../components";
import { getDollarValue } from "../../helpers/currency";
import { StyledLineStack } from "./styles";

const COLORS = {
  green: "#16a34a",
  blue: "#3478F6",
  purple: "#6f5af0",
};

const trimZero = (n) => n.toFixed(1).replace(/\.0$/, "");

const formatCompactDollars = (v) => {
  if (v == null || Number.isNaN(v)) return "";
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `$${trimZero(v / 1_000_000)}M`;
  if (abs >= 1_000) return `$${trimZero(v / 1_000)}k`;
  return `$${Math.round(v)}`;
};

export const LinearGradient = ({ color }) => {
  return (
    <svg height="0" width="0">
      <defs>
        <linearGradient
          id={`LinearGradient-${color}`}
          x1="0%"
          y1="0%"
          x2="100%"
          y2="100%"
          gradientTransform="rotate(45)"
        >
          <stop offset="0%" stopColor={color} stop-opacity="0.225" />
          <stop offset="65%" stopColor={color} stop-opacity="0" />
        </linearGradient>
      </defs>
    </svg>
  );
};

const DashboardRevenueChart = ({
  series = [],
  isLoading,
  color = COLORS.green,
  emptyIcon = "Activity",
  emptyTitle = "No revenue yet",
  emptySubtitle = "Once revenue is recorded it'll roll up here over the last 12 months.",
}) => {
  const { labels, values, total } = useMemo(() => {
    const lbls = series.map((b) => dayjs(b.date).format("MMM DD"));
    const vals = series.map((b) => Number(b.revenue) || 0);
    const tot = vals.reduce((s, v) => s + v, 0);
    return { labels: lbls, values: vals, total: tot };
  }, [series]);

  if (!isLoading && total === 0) {
    return (
      <DashboardEmptyState
        icon={emptyIcon}
        title={emptyTitle}
        subtitle={emptySubtitle}
      />
    );
  }

  return (
    <StyledLineStack color={color}>
      <LinearGradient color={color} />
      <LineChart
        height={300}
        series={[
          {
            data: values,
            area: true,
            color,
            valueFormatter: (v) => getDollarValue(v || 0),
            showMark: ({ index }) => values?.length - 1 === index,
          },
        ]}
        xAxis={[{ scaleType: "point", data: labels }]}
        yAxis={[{ valueFormatter: formatCompactDollars }]}
        margin={{ top: 10, bottom: 30, left: 50, right: 10 }}
        grid={{ horizontal: true }}
      />
    </StyledLineStack>
  );
};

export default DashboardRevenueChart;
